import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Header } from '../../components/layout/Header';
import { API } from '../../utils/api';
import { GuidanceCard } from '../../components/GuidanceCard';
import { trackTaskCompleted, trackGuidanceOpened } from '../../utils/analytics';
import axios from 'axios';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Circle,
  Warning,
  Info,
  NotePencil,
  Lock,
  Star
} from '@phosphor-icons/react';
import { Textarea } from '../../components/ui/textarea';
import { CollapsibleSection } from '../../components/TaskSection';
import { ScrollToTop } from '../../components/ScrollToTop';

export default function ChecklistDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { isAuthenticated, isPaid } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedNotes, setSavedNotes] = useState('');
  const [notesMessage, setNotesMessage] = useState('');
  const [statusError, setStatusError] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const response = await axios.get(`${API}/checklist`);
        const foundItem = response.data.items.find(i => i.item_id === itemId);
        if (foundItem) {
          setItem(foundItem);
          setNotes(foundItem.notes || '');
          setSavedNotes(foundItem.notes || '');
          setNotesMessage('');
        } else {
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Failed to fetch checklist:', err);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchChecklist();
    }
  }, [isAuthenticated, itemId, navigate]);

  const updateStatus = async (newStatus) => {
    if (!isPaid) return; // Only paid users can update status
    setUpdatingStatus(true);
    setStatusError(false);
    try {
      await axios.patch(`${API}/checklist/item/${itemId}`,
        { item_id: itemId, status: newStatus }
      );
      setItem(prev => ({ ...prev, status: newStatus }));

      // Track task completion
      if (newStatus === 'completed') {
        const title = item?.title_en || item?.title || itemId;
        trackTaskCompleted(itemId, title);
      }
    } catch (err) {
      setStatusError(true);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const saveNotes = async () => {
    if (!isPaid) return; // Only paid users can save notes
    setSavingNotes(true);
    setNotesMessage('');
    const submittedNotes = notes;
    try {
      await axios.patch(`${API}/checklist/item/${itemId}/notes`,
        { notes: submittedNotes }
      );
      setSavedNotes(submittedNotes);
      setNotesMessage('saved');
    } catch (err) {
      setNotesMessage('error');
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-pp-bg">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-pp-navy border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!item) {
    return null;
  }

  const title = language === 'fr' ? item.title_fr : item.title_en;
  const description = language === 'fr' ? item.description_fr : item.description_en;

  // Sections that are available to all users (free preview)
  const freeSections = [
    { key: 'whatIs', icon: Info, content: language === 'fr' ? item.what_is_fr : item.what_is_en },
  ];

  // Premium-only sections
  const practicalSections = item.practical_guide || [];
  const premiumSections = [
    { key: 'whoNeeds', icon: Info, content: language === 'fr' ? item.who_needs_fr : item.who_needs_en },
    { key: 'when', icon: Clock, content: language === 'fr' ? item.when_fr : item.when_en },
    { key: 'whatToPrepare', icon: Info, content: language === 'fr' ? item.what_to_prepare_fr : item.what_to_prepare_en },
    { key: 'commonMistakes', icon: Warning, content: language === 'fr' ? item.common_mistakes_fr : item.common_mistakes_en },
    { key: 'nextStep', icon: CheckCircle, content: language === 'fr' ? item.next_step_fr : item.next_step_en },
  ];

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      <ScrollToTop />

      <main className="pt-24 pb-12">
        <div className="pp-container max-w-3xl">
          {/* Back Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-pp-text-secondary hover:text-pp-text mb-6 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={18} />
            {t('checklistItem.back')}
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="pp-card mb-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <p className="pp-label" data-testid="task-plan-label">
                      {language === 'fr' ? 'VOTRE PLAN D’INSTALLATION' : 'YOUR SETTLEMENT PLAN'}
                    </p>
                    {item.is_optional && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium border border-slate-200">
                        {language === 'fr' ? 'Selon vos besoins' : 'As needed'}
                      </span>
                    )}
                    {isPaid && !item.is_optional && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-600 text-xs font-medium">
                        <Star size={10} weight="fill" />
                        {t('premium.badge')}
                      </span>
                    )}
                  </div>
                  <h1 className="pp-heading-3 text-pp-text [overflow-wrap:anywhere]" data-testid="item-title">
                    {title}
                  </h1>
                </div>
                <StatusBadge status={item.status} t={t} isOptional={item.is_optional} language={language} />
              </div>
              <p className="text-pp-text-secondary">{description}</p>

              {/* Optional item note */}
              {item.is_optional && (
                <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <p className="text-sm text-slate-500">
                    {language === 'fr'
                      ? 'Cette étape peut être prioritaire selon votre situation. Elle ne compte pas dans votre progression principale.'
                      : 'This step may be a priority for your situation. It is not included in your core progress.'
                    }
                  </p>
                </div>
              )}
            </div>

            {item.next_action_en && <section className="pp-card mb-6 border border-[#80B9AD]" data-testid="task-next-action">
              <h2 className="font-semibold mb-2" data-testid="task-next-action-heading">{language === 'fr' ? 'Votre prochaine action' : 'Your next action'}</h2>
              <p className="text-pp-text-secondary [overflow-wrap:anywhere]" data-testid="task-next-action-text">{item[`next_action_${language}`]}</p>
              {item[`timing_note_${language}`] && <p className="text-sm text-slate-600 mt-3" data-testid="task-timing-note">{item[`timing_note_${language}`]}</p>}
            </section>}
            {item.sources?.length > 0 && <section className="pp-card mb-6" data-testid="task-official-sources">
              <h2 className="font-semibold mb-2" data-testid="task-sources-heading">{language === 'fr' ? 'Sources officielles' : 'Official sources'}</h2>
              <ul className="space-y-2">{item.sources.map((source, index) => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="text-[#236C64] underline underline-offset-4 focus-ring [overflow-wrap:anywhere]" data-testid={`task-source-link-${index + 1}`}>{source.label}</a></li>)}</ul>
              {item.reviewed_on && <p className="text-xs text-slate-600 mt-3" data-testid="task-review-date">{language === 'fr' ? 'Contenu vérifié le' : 'Content reviewed'} {item.reviewed_on}. {language === 'fr' ? 'Vérifiez les exigences actuelles auprès de l’organisme.' : 'Confirm current requirements with the organization.'}</p>}
            </section>}

            {/* Free user upgrade prompt - Show what they're missing */}
            {!isPaid && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-pp-navy/5 to-pp-navy/10 border border-pp-navy/10"
                data-testid="upgrade-prompt"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-pp-navy/10 flex items-center justify-center flex-shrink-0">
                    <Lock size={20} className="text-pp-navy" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-pp-text mb-2">{t('premium.upgradeTitle')}</h3>

                    {/* Show what's locked - clear value */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-pp-text-muted">
                        <Lock size={14} />
                        <span>{language === 'fr' ? t('premium.lockedItems.mistakes') : t('premium.lockedItems.mistakes')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-pp-text-muted">
                        <Lock size={14} />
                        <span>{language === 'fr' ? t('premium.lockedItems.nextSteps') : t('premium.lockedItems.nextSteps')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-pp-text-muted">
                        <Lock size={14} />
                        <span>{language === 'fr' ? t('premium.lockedItems.documents') : t('premium.lockedItems.documents')}</span>
                      </div>
                    </div>

                    <p className="text-sm text-pp-text-secondary mb-4">
                      {t('premium.upgradeDescription')}
                    </p>
                    <Link
                      to="/pricing"
                      className="pp-btn-primary text-sm !py-2.5 !px-5"
                    >
                      {t('premium.lockedButton')}
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Free Content Section - Available to all */}
            <div className="space-y-4 mb-6">
              {freeSections.map(({ key, icon: Icon, content }) => (
                <CollapsibleSection
                  key={key}
                  title={t(`checklistItem.${key}`)}
                  icon={Icon}
                  content={content}
                  defaultOpen={true}
                  locked={false}
                  testId={`task-section-${key}`}
                />
              ))}
            </div>

            {/* Premium Content Sections */}
            <div className="space-y-4 mb-6">
              {practicalSections.length > 0 ? practicalSections.map(section => {
                const List = section.ordered ? 'ol' : 'ul';
                return <CollapsibleSection
                  key={section.id}
                  title={section[`title_${language}`]}
                  icon={Info}
                  content={<List className={`${section.ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-3`} data-testid={`task-guide-${section.id}-list`}>{(section[`points_${language}`] || []).map((point, index) => <li key={index} data-testid={`task-guide-${section.id}-point-${index + 1}`}>{point}</li>)}</List>}
                  defaultOpen={isPaid && section.id === 'prepare'}
                  locked={!isPaid}
                  language={language}
                  testId={`task-guide-${section.id}`}
                />;
              }) : premiumSections.map(({ key, icon: Icon, content }) => (
                <CollapsibleSection
                  key={key}
                  title={t(`checklistItem.${key}`)}
                  icon={Icon}
                  content={content}
                  defaultOpen={isPaid && (key === 'whatToPrepare')}
                  locked={!isPaid}
                  language={language}
                  testId={`task-section-${key}`}
                />
              ))}
            </div>

            {/* PaperPal Guidance Card - Always visible */}
            <GuidanceCard
              contextType="checklist_step"
              contextId={itemId}
              stepTitle={title}
              stepDescription={description}
            />

            {statusError && <p role="alert" className="mb-4 text-sm text-red-700" data-testid="status-save-error">{language === 'fr' ? 'Le statut n’a pas été enregistré. Réessayez.' : 'The status was not saved. Please try again.'}</p>}
            {/* Notes - Premium only */}
            {isPaid ? (
              <div className="pp-card mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <NotePencil size={20} className="text-pp-navy" weight="duotone" />
                  <h3 className="font-medium text-pp-text">
                    {language === 'fr' ? 'Mes notes' : 'My Notes'}
                  </h3>
                </div>
                <Textarea
                  value={notes}
                  onChange={(e) => { setNotes(e.target.value); setNotesMessage(''); }}
                  aria-label={language === 'fr' ? 'Mes notes' : 'My notes'}
                  disabled={savingNotes}
                  placeholder={language === 'fr'
                    ? 'Écrivez vos notes, prix, documents ou rappels ici…'
                    : 'Write your notes, prices, documents, or reminders here…'
                  }
                  className="pp-input min-h-[120px] mb-3 resize-none"
                  data-testid="notes-input"
                />
                <div className="flex items-center justify-between">
                  <p role="status" aria-live="polite" className="text-xs text-slate-600 max-w-[65%]" data-testid="notes-save-status">
                    {savingNotes ? (language === 'fr' ? 'Enregistrement…' : 'Saving…') : notesMessage === 'error' ? (language === 'fr' ? 'Échec de l’enregistrement. Vos notes sont encore ici. Réessayez.' : 'Could not save. Your notes are still here. Try again.') : notes !== savedNotes ? (language === 'fr' ? 'Modifications non enregistrées' : 'Unsaved changes') : notesMessage === 'saved' ? (language === 'fr' ? 'Notes enregistrées' : 'Notes saved') : (language === 'fr' ? 'Cliquez sur Enregistrer après modification.' : 'Click Save after making changes.')}
                  </p>
                  <button
                    onClick={saveNotes}
                    disabled={savingNotes || notes === savedNotes}
                    className="pp-btn-secondary text-sm"
                    data-testid="save-notes-button"
                  >
                    {savingNotes ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="pp-card mb-6 opacity-60">
                <div className="flex items-center gap-2 mb-3">
                  <NotePencil size={20} className="text-pp-text-muted" />
                  <h3 className="font-medium text-pp-text-muted">
                    {language === 'fr' ? 'Mes notes' : 'My Notes'}
                  </h3>
                  <Lock size={14} className="text-pp-text-muted ml-auto" />
                </div>
                <div className="p-4 rounded-xl bg-pp-secondary/50 text-center">
                  <p className="text-sm text-pp-text-muted">
                    {language === 'fr' ? 'Notes personnelles disponibles avec Premium' : 'Personal notes available with Premium'}
                  </p>
                </div>
              </div>
            )}

            {/* Status Actions - Premium only */}
            {isPaid ? (
              <div className="pp-card">
                <h3 className="font-medium text-pp-text mb-4">
                  {language === 'fr' ? 'Mettre à jour le statut' : 'Update status'}
                </h3>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => updateStatus('not_started')}
                    disabled={updatingStatus || item.status === 'not_started'}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all font-medium ${
                      item.status === 'not_started'
                        ? 'border-gray-400 bg-gray-100 text-gray-700 ring-2 ring-gray-300 ring-offset-1'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid="status-not-started"
                  >
                    <Circle size={18} weight={item.status === 'not_started' ? 'fill' : 'regular'} />
                    {t('checklistItem.markNotStarted')}
                    {item.status === 'not_started' && <span className="text-xs ml-1">✓</span>}
                  </button>
                  <button
                    onClick={() => updateStatus('in_progress')}
                    disabled={updatingStatus || item.status === 'in_progress'}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all font-medium ${
                      item.status === 'in_progress'
                        ? 'border-orange-400 bg-orange-100 text-orange-700 ring-2 ring-orange-300 ring-offset-1'
                        : 'border-gray-200 text-gray-500 hover:border-orange-300 hover:bg-orange-50'
                    }`}
                    data-testid="status-in-progress"
                  >
                    <Clock size={18} weight={item.status === 'in_progress' ? 'fill' : 'regular'} />
                    {t('checklistItem.markInProgress')}
                    {item.status === 'in_progress' && <span className="text-xs ml-1">✓</span>}
                  </button>
                  <button
                    onClick={() => updateStatus('completed')}
                    disabled={updatingStatus || item.status === 'completed'}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all font-medium ${
                      item.status === 'completed'
                        ? 'border-[#134E4A] bg-[#134E4A]/10 text-[#134E4A] ring-2 ring-[#134E4A]/30 ring-offset-1'
                        : 'border-gray-200 text-gray-500 hover:border-[#134E4A]/50 hover:bg-[#134E4A]/5'
                    }`}
                    data-testid="status-completed"
                  >
                    <CheckCircle size={18} weight="fill" />
                    {t('checklistItem.markComplete')}
                    {item.status === 'completed' && <span className="text-xs ml-1">✓</span>}
                  </button>
                </div>
                {/* Current status indicator */}
                <p className="text-xs text-pp-text-muted mt-3">
                {language === 'fr' ? 'Statut actuel : ' : 'Current status: '}
                  <span className={`font-medium ${
                    item.status === 'completed' ? 'text-[#134E4A]' :
                    item.status === 'in_progress' ? 'text-orange-600' :
                    'text-gray-600'
                  }`}>
                    {t(`dashboard.status.${item.status || 'not_started'}`)}
                  </span>
                </p>
              </div>
            ) : (
              <div className="pp-card opacity-60">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-pp-text-muted">
                    {language === 'fr' ? 'Mettre à jour le statut' : 'Update status'}
                  </h3>
                  <Lock size={16} className="text-pp-text-muted" />
                </div>
                <div className="p-4 rounded-xl bg-pp-secondary/50 text-center">
                  <p className="text-sm text-pp-text-muted">
                    {language === 'fr' ? 'Suivi de statut disponible avec Premium' : 'Status tracking available with Premium'}
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}

function StatusBadge({ status, t, isOptional = false, language = 'en' }) {
  // For optional items that haven't been started, show "Optional" instead of "Not Started"
  if (isOptional && status === 'not_started') {
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-500 border border-slate-200">
        {language === 'fr' ? 'Selon vos besoins' : 'As needed'}
      </span>
    );
  }

  switch (status) {
    case 'completed':
      return <span className="pp-success-badge">{t('dashboard.status.completed')}</span>;
    case 'in_progress':
      return <span className="pp-warning-badge">{t('dashboard.status.in_progress')}</span>;
    default:
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-pp-bg-muted text-pp-text-muted border border-pp-border">
          {t('dashboard.status.not_started')}
        </span>
      );
  }
}